import { useState, useEffect, useCallback } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { InventoryItem } from '../../../types/inventory';

export interface UseInventoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: any) => Promise<void>;
  itemTypes: { id: number; name: string }[];
  itemMaterials: { id: number; name: string }[];
  branches: { id: number; name: string }[];
  itemStatuses: { id: string; name: string }[];
  editItem?: InventoryItem | null;
}

export const useInventoryDrawer = ({
  isOpen,
  onClose,
  onSave,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  itemTypes,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  itemMaterials,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  branches,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  itemStatuses,
  editItem = null,
}: UseInventoryDrawerProps) => {
  // Estados del drawer
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formProgress, setFormProgress] = useState<number>(25);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Datos del formulario
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    type: '',
    createdAt: new Date().toISOString().split('T')[0],
    recordCode: '',
    status: 'Disponible',
    updatedAt: '',
    location: '',
    material: '',
    price: '',
    locationName: '',
  });

  // Configuración de pasos
  const steps = [
    { id: 1, name: 'Información Básica', icon: 'Tag' },
    { id: 2, name: 'Categoría', icon: 'Layers' },
    { id: 3, name: 'Ubicación', icon: 'LocationOn' },
    { id: 4, name: 'Estado', icon: 'Business' },
  ];

  // Inicializar formulario cuando se abre el drawer
  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        setFormData({
          id: editItem.id || '',
          name: editItem.name || '',
          type: editItem.type?.toString() || '',
          createdAt: editItem.createdAt || new Date().toISOString().split('T')[0],
          recordCode: editItem.recordCode || '',
          status: editItem.status || 'Disponible',
          updatedAt: editItem.updatedAt || '',
          location: editItem.location?.toString() || '',
          material: editItem.material?.toString() || '',
          price: editItem.price?.toString() || '',
          locationName: editItem.locationName || '',
        });

        // Normalizar tags: convertir objetos a strings si es necesario
        const normalizedTags = Array.isArray(editItem.tags)
          ? editItem.tags.map((tag: any) => (typeof tag === 'string' ? tag : tag?.name || ''))
          : [];
        setSelectedTags(normalizedTags);
      } else {
        // Reset form for new inventory item
        setFormData({
          id: '',
          name: '',
          type: '',
          createdAt: new Date().toISOString().split('T')[0],
          recordCode: '',
          status: 'Disponible',
          updatedAt: '',
          location: '',
          material: '',
          price: '',
          locationName: '',
        });
        setSelectedTags([]);
      }

      setCurrentStep(1);
      setFormProgress(25);
      setShowConfirmation(false);
      setShowCancelConfirmation(false);
    }
  }, [isOpen, editItem]);

  // Navegación entre pasos
  const nextStep = useCallback(() => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setFormProgress(Math.min(100, (currentStep + 1) * 25));
    }
  }, [currentStep, steps.length]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setFormProgress(Math.max(25, (currentStep - 1) * 25));
    }
  }, [currentStep]);

  // Handlers de formulario
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleSelectChange = useCallback((e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Manejo de tags
  const addTag = useCallback(
    (tag: string) => {
      if (!selectedTags.includes(tag)) {
        setSelectedTags([...selectedTags, tag]);
      }
    },
    [selectedTags],
  );

  const removeTag = useCallback(
    (tag: string) => {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    },
    [selectedTags],
  );

  // Validación por paso
  const validateCurrentStep = useCallback(() => {
    switch (currentStep) {
      case 1: // Basic Information
        return formData.name.trim() !== '' && formData.id.trim() !== '';
      case 2: // Category
        return true;
      case 3: // Location
        return true;
      case 4: // Status
        return true;
      default:
        return false;
    }
  }, [currentStep, formData.name, formData.id]);

  // Guardar item
  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);

      // Add selected tags to form data before saving
      const itemDataWithTags = {
        ...formData,
        tags: selectedTags,
      };

      await onSave(itemDataWithTags);
      setShowConfirmation(false);
      onClose();
    } catch (error) {
      console.error('Error saving inventory item:', error);
    } finally {
      setIsSaving(false);
    }
  }, [formData, selectedTags, onSave, onClose]);

  // Manejo de cierre
  const handleCloseClick = useCallback(() => {
    setShowCancelConfirmation(true);
  }, []);

  const cancelOperation = useCallback(() => {
    setShowCancelConfirmation(false);
    onClose();
  }, [onClose]);

  // Handlers de confirmación
  const handleConfirmSave = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const handleCancelSave = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const handleCancelCancel = useCallback(() => {
    setShowCancelConfirmation(false);
  }, []);

  // Manejo de tecla Enter para agregar tags
  const handleTagKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
        addTag(e.currentTarget.value.trim());
        e.currentTarget.value = '';
        e.preventDefault();
      }
    },
    [addTag],
  );

  return {
    // Estados
    currentStep,
    formProgress,
    showConfirmation,
    showCancelConfirmation,
    isSaving,
    selectedTags,
    formData,
    steps,

    // Handlers de navegación
    nextStep,
    prevStep,

    // Handlers de formulario
    handleInputChange,
    handleSelectChange,

    // Handlers de tags
    addTag,
    removeTag,
    handleTagKeyDown,

    // Validación
    validateCurrentStep,

    // Handlers de acciones
    handleSave,
    handleCloseClick,
    cancelOperation,
    handleConfirmSave,
    handleCancelSave,
    handleCancelCancel,
  };
};
